// Functional SPA scope; build:release requires VITE_OAUTH_SCOPE to equal it (docs/P9-READINESS.md).
import RELEASE_OAUTH_SCOPE from './releaseOAuthScope.json' with { type: 'json' };

export type OAuthTokens = {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in?: number;
  scope?: string;
};

export type OAuthConfiguration = {
  apiHost: string;
  clientId: string;
  redirectUri: string;
  scope: string;
};

export type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export type FetchLike = typeof fetch;

const PKCE_STATE_KEY = 'opstrack.oauth.pkce.state';
const PKCE_VERIFIER_KEY = 'opstrack.oauth.pkce.verifier';
const ACCESS_TOKEN_KEY = 'opstrack.oauth.access_token';
const REFRESH_TOKEN_KEY = 'opstrack.oauth.refresh_token';
const EXPIRES_AT_KEY = 'opstrack.oauth.expires_at';
const RETURN_PATH_KEY = 'opstrack.oauth.return_path';
const CALLBACK_MARKER_KEY = 'opstrack.oauth.callback.marker';
const callbackExchanges = new Map<string, Promise<OAuthTokens>>();

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function generateSecureValue(byteLength = 32): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

export async function createCodeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier)
  );

  return base64Url(new Uint8Array(digest));
}

export async function createPkceRequest(storage: StorageLike) {
  const state = generateSecureValue();
  const verifier = generateSecureValue(64);
  const challenge = await createCodeChallenge(verifier);

  storage.setItem(PKCE_STATE_KEY, state);
  storage.setItem(PKCE_VERIFIER_KEY, verifier);

  return { state, verifier, challenge };
}

export function clearPkceRequest(storage: StorageLike) {
  storage.removeItem(PKCE_STATE_KEY);
  storage.removeItem(PKCE_VERIFIER_KEY);
}

export function validatePkceState(storage: StorageLike, state: string | null) {
  const expectedState = storage.getItem(PKCE_STATE_KEY);
  return !!state && !!expectedState && state === expectedState;
}

export function getCodeVerifier(storage: StorageLike) {
  return storage.getItem(PKCE_VERIFIER_KEY);
}

export function saveOAuthTokens(storage: StorageLike, tokens: OAuthTokens) {
  storage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
  storage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
  if (tokens.expires_in !== undefined) {
    storage.setItem(
      EXPIRES_AT_KEY,
      String(Date.now() + tokens.expires_in * 1000)
    );
  }
}

export function getAccessToken(storage: StorageLike) {
  return storage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(storage: StorageLike) {
  return storage.getItem(REFRESH_TOKEN_KEY);
}

export function clearOAuthTokens(storage: StorageLike) {
  storage.removeItem(ACCESS_TOKEN_KEY);
  storage.removeItem(REFRESH_TOKEN_KEY);
  storage.removeItem(EXPIRES_AT_KEY);
}

export function getOAuthExpiresAt(storage: StorageLike) {
  return storage.getItem(EXPIRES_AT_KEY);
}

function callbackMarker(storage: StorageLike, code: string) {
  return storage.getItem(CALLBACK_MARKER_KEY) === code;
}

export async function completeOAuthCallbackOnce(
  config: OAuthConfiguration,
  params: URLSearchParams,
  storage: StorageLike,
  request: FetchLike = fetch
) {
  const code = params.get('code');
  if (!code) {
    return completeOAuthCallback(config, params, storage, request);
  }

  if (callbackMarker(storage, code)) {
    return callbackExchanges.get(code) ?? null;
  }

  storage.setItem(CALLBACK_MARKER_KEY, code);
  const exchange = (async () => {
    const tokens = await completeOAuthCallback(config, params, storage, request);
    return tokens;
  })();
  callbackExchanges.set(code, exchange);
  try {
    return await exchange;
  } catch (error) {
    storage.removeItem(CALLBACK_MARKER_KEY);
    throw error;
  } finally {
    callbackExchanges.delete(code);
  }
}

export function saveOAuthReturnPath(storage: StorageLike, path: string) {
  if (path.startsWith('/')) {
    storage.setItem(RETURN_PATH_KEY, path);
  }
}

export function consumeOAuthReturnPath(storage: StorageLike) {
  const path = storage.getItem(RETURN_PATH_KEY);
  storage.removeItem(RETURN_PATH_KEY);
  return path?.startsWith('/') ? path : null;
}

export function buildAuthorizeUrl(
  config: OAuthConfiguration,
  state: string,
  challenge: string
) {
  const url = oauthEndpointUrl(config, '/o/authorize/');
  url.search = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    response_type: 'code',
    scope: config.scope,
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256'
  }).toString();

  return url.toString();
}

export function getOAuthConfiguration(): OAuthConfiguration {
  const apiHost = import.meta.env.VITE_API_HOST;
  const clientId = import.meta.env.VITE_OAUTH_CLIENT_ID;
  const redirectUri =
    import.meta.env.VITE_OAUTH_REDIRECT_URI ??
    `${import.meta.env.VITE_FRONTEND_HOST ?? window.location.origin}/oauth/callback`;

  if (!apiHost || !clientId) {
    throw new Error('OAuth configuration is incomplete');
  }

  return {
    apiHost,
    clientId,
    redirectUri,
    scope: import.meta.env.VITE_OAUTH_SCOPE ?? RELEASE_OAUTH_SCOPE.join(' ')
  };
}

function tokenUrl(config: OAuthConfiguration, endpoint: string) {
  return oauthEndpointUrl(config, endpoint).toString();
}

/**
 * Append an OAuth endpoint to the configured API mount path. API Gateway
 * stages and custom-domain mappings can mount Django below a prefix such as
 * `/dev`; a leading-slash URL passed to `new URL` would silently drop it.
 */
function oauthEndpointUrl(config: OAuthConfiguration, endpoint: string) {
  const apiBase = new URL(config.apiHost);
  const mountPath = apiBase.pathname.replace(/\/+$/, '');
  const endpointPath = endpoint.replace(/^\/+/, '');

  return new URL(`${mountPath}/${endpointPath}`, apiBase.origin);
}

function tokenResponse(data: unknown): OAuthTokens {
  const tokens = data as Partial<OAuthTokens>;

  if (
    !tokens.access_token ||
    !tokens.refresh_token ||
    tokens.token_type?.toLowerCase() !== 'bearer'
  ) {
    throw new Error('OAuth token response is incomplete');
  }

  return tokens as OAuthTokens;
}

export async function exchangeAuthorizationCode(
  config: OAuthConfiguration,
  code: string,
  verifier: string,
  request: FetchLike = fetch
) {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.clientId,
    code,
    redirect_uri: config.redirectUri,
    code_verifier: verifier
  });
  const response = await request(tokenUrl(config, '/o/token/'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
    credentials: 'omit'
  });

  if (!response.ok) {
    throw new Error('OAuth token exchange failed');
  }

  return tokenResponse(await response.json());
}

export async function refreshOAuthTokens(
  config: OAuthConfiguration,
  refreshToken: string,
  request: FetchLike = fetch
) {
  const response = await request(tokenUrl(config, '/o/token/'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: config.clientId,
      refresh_token: refreshToken
    }),
    credentials: 'omit'
  });

  if (!response.ok) {
    throw new Error('OAuth token refresh failed');
  }

  return tokenResponse(await response.json());
}

export async function completeOAuthCallback(
  config: OAuthConfiguration,
  params: URLSearchParams,
  storage: StorageLike,
  request: FetchLike = fetch
) {
  const code = params.get('code');
  const state = params.get('state');
  const verifier = getCodeVerifier(storage);

  if (!code) {
    clearPkceRequest(storage);
    throw new Error('OAuth callback did not include an authorization code');
  }

  if (!validatePkceState(storage, state) || !verifier) {
    clearPkceRequest(storage);
    throw new Error('OAuth callback state is invalid');
  }

  try {
    const tokens = await exchangeAuthorizationCode(config, code, verifier, request);
    saveOAuthTokens(storage, tokens);
    return tokens;
  } finally {
    clearPkceRequest(storage);
  }
}

export async function revokeOAuthToken(
  config: OAuthConfiguration,
  token: string,
  hint: 'access_token' | 'refresh_token',
  request: FetchLike = fetch
) {
  await request(tokenUrl(config, '/o/revoke_token/'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      token,
      token_type_hint: hint,
      client_id: config.clientId
    }),
    credentials: 'omit'
  });
}
